import React, { useState, useEffect, useCallback } from "react";
import {
    Shield,
    Key,
    Building2,
    Loader2,
    CheckCircle,
    AlertCircle,
    Eye,
    EyeOff,
    Copy,
    ExternalLink,
    RefreshCw,
    Trash2,
    Save,
    TestTube,
    ToggleLeft,
    ToggleRight,
    Info,
    Globe,
    Users,
    Lock
} from "lucide-react";
import {
    fetchOrgSSOConfig,
    createOrgSSOConfig,
    updateOrgSSOConfig,
    deleteOrgSSOConfig,
    testSSOConnection,
    enableOrgSSO,
    disableOrgSSO,
    SSO_PROVIDERS,
    SSO_STATUS,
    DEFAULT_ATTRIBUTE_MAPPINGS,
    getSSOProviderInfo
} from "../../api/sso";

// Provider options for dropdown
const PROVIDER_OPTIONS = [
    { value: "okta", label: "Okta", description: "Enterprise identity management" },
    { value: "azure", label: "Microsoft Azure AD", description: "Microsoft cloud identity" },
    { value: "google", label: "Google Workspace", description: "Google cloud identity" },
    { value: "auth0", label: "Auth0", description: "Flexible identity platform" },
    { value: "generic", label: "Generic OIDC", description: "Any OIDC-compliant provider" }
];

// Help text for each provider
const PROVIDER_HELP = {
    okta: {
        issuerUrl: "Your Okta domain (e.g., https://your-company.okta.com)",
        docsUrl: "https://developer.okta.com/docs/guides/implement-oauth-for-okta/main/"
    },
    azure: {
        issuerUrl: "Azure AD endpoint (e.g., https://login.microsoftonline.com/{tenant-id}/v2.0)",
        docsUrl: "https://learn.microsoft.com/en-us/azure/active-directory/develop/"
    },
    google: {
        issuerUrl: "https://accounts.google.com",
        docsUrl: "https://developers.google.com/identity/openid-connect/openid-connect"
    },
    auth0: {
        issuerUrl: "Your Auth0 domain (e.g., https://your-tenant.auth0.com)",
        docsUrl: "https://auth0.com/docs/get-started/applications"
    },
    generic: {
        issuerUrl: "The OIDC issuer URL from your identity provider",
        docsUrl: null
    }
};

const OrgSSOSettings = ({ organization, onClose, userRole, canEdit = false }) => {
    // Config state
    const [config, setConfig] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState("");
    const [success, setSuccess] = useState("");

    // Form state
    const [isEditing, setIsEditing] = useState(false);
    const [isSaving, setIsSaving] = useState(false);
    const [isTesting, setIsTesting] = useState(false);
    const [isDeleting, setIsDeleting] = useState(false);
    const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
    const [showClientSecret, setShowClientSecret] = useState(false);
    const [testResult, setTestResult] = useState(null);

    // Form fields
    const [formData, setFormData] = useState({
        provider: "okta",
        issuer_url: "",
        client_id: "",
        client_secret: "",
        sso_required: false,
        auto_provision: true,
        default_role: "member",
        allowed_domains: [],
        attribute_mapping: DEFAULT_ATTRIBUTE_MAPPINGS.okta,
        scopes: ["openid", "email", "profile"]
    });

    const [domainInput, setDomainInput] = useState("");

    const orgId = organization?.id || organization?.org_id;

    // Load config
    const loadConfig = useCallback(async () => {
        if (!orgId) return;

        setIsLoading(true);
        setError("");
        try {
            const data = await fetchOrgSSOConfig(orgId);
            setConfig(data);
            if (data) {
                setFormData({
                    provider: data.provider || "okta",
                    issuer_url: data.issuer_url || "",
                    client_id: data.client_id || "",
                    client_secret: "", // Never populated from backend
                    sso_required: data.sso_required || false,
                    auto_provision: data.auto_provision !== false,
                    default_role: data.default_role || "member",
                    allowed_domains: data.allowed_domains || [],
                    attribute_mapping: data.attribute_mapping || DEFAULT_ATTRIBUTE_MAPPINGS[data.provider || "okta"],
                    scopes: data.scopes || ["openid", "email", "profile"]
                });
            }
        } catch (err) {
            console.error("Failed to load SSO config:", err);
            setError("Unable to load SSO configuration.");
        } finally {
            setIsLoading(false);
        }
    }, [orgId]);

    useEffect(() => {
        loadConfig();
    }, [loadConfig]);

    // Handle form changes
    const handleChange = (field, value) => {
        setFormData(prev => ({ ...prev, [field]: value }));

        // Update attribute mapping when provider changes
        if (field === "provider") {
            setFormData(prev => ({
                ...prev,
                provider: value,
                attribute_mapping: DEFAULT_ATTRIBUTE_MAPPINGS[value] || DEFAULT_ATTRIBUTE_MAPPINGS.generic
            }));
        }
    };

    // Add domain
    const handleAddDomain = () => {
        const domain = domainInput.trim().toLowerCase();
        if (!domain) return;
        if (formData.allowed_domains.includes(domain)) {
            setError("Domain already added");
            return;
        }
        // Basic domain validation
        if (!/^[a-z0-9]+([\-\.][a-z0-9]+)*\.[a-z]{2,}$/.test(domain)) {
            setError("Invalid domain format");
            return;
        }
        setFormData(prev => ({
            ...prev,
            allowed_domains: [...prev.allowed_domains, domain]
        }));
        setDomainInput("");
        setError("");
    };

    // Remove domain
    const handleRemoveDomain = (domain) => {
        setFormData(prev => ({
            ...prev,
            allowed_domains: prev.allowed_domains.filter(d => d !== domain)
        }));
    };

    // Save config
    const handleSave = async () => {
        if (!orgId) return;

        // Validation
        if (!formData.issuer_url.trim()) {
            setError("Issuer URL is required");
            return;
        }
        if (!formData.client_id.trim()) {
            setError("Client ID is required");
            return;
        }
        if (!config && !formData.client_secret.trim()) {
            setError("Client Secret is required for new configuration");
            return;
        }

        setIsSaving(true);
        setError("");
        setSuccess("");

        try {
            const payload = {
                provider: formData.provider,
                issuer_url: formData.issuer_url.trim(),
                client_id: formData.client_id.trim(),
                sso_required: formData.sso_required,
                auto_provision: formData.auto_provision,
                default_role: formData.default_role,
                allowed_domains: formData.allowed_domains,
                attribute_mapping: formData.attribute_mapping
            };

            // Only include client_secret if provided (for create or update)
            if (formData.client_secret.trim()) {
                payload.client_secret = formData.client_secret.trim();
            }

            if (config) {
                await updateOrgSSOConfig(orgId, payload);
                setSuccess("SSO configuration updated successfully");
            } else {
                await createOrgSSOConfig(orgId, payload);
                setSuccess("SSO configuration created successfully");
            }

            setIsEditing(false);
            await loadConfig();
        } catch (err) {
            console.error("Failed to save SSO config:", err);
            setError(err.message || "Failed to save SSO configuration");
        } finally {
            setIsSaving(false);
        }
    };

    // Test connection
    const handleTest = async () => {
        if (!orgId) return;

        setIsTesting(true);
        setTestResult(null);
        setError("");

        try {
            const result = await testSSOConnection(orgId);
            setTestResult(result);
            if (result.success) {
                setSuccess("SSO connection test passed!");
            }
        } catch (err) {
            console.error("SSO test failed:", err);
            setTestResult({ success: false, error: err.message });
            setError(err.message || "SSO connection test failed");
        } finally {
            setIsTesting(false);
        }
    };

    // Toggle SSO enabled/disabled
    const handleToggleEnabled = async () => {
        if (!orgId || !config) return;

        setIsSaving(true);
        setError("");
        setSuccess("");

        try {
            if (config.status === SSO_STATUS.ENABLED) {
                await disableOrgSSO(orgId);
                setSuccess("SSO disabled");
            } else {
                await enableOrgSSO(orgId);
                setSuccess("SSO enabled");
            }
            await loadConfig();
        } catch (err) {
            console.error("Failed to toggle SSO:", err);
            setError(err.message || "Failed to toggle SSO");
        } finally {
            setIsSaving(false);
        }
    };

    // Delete config
    const handleDelete = async () => {
        if (!orgId) return;

        setIsDeleting(true);
        setError("");

        try {
            await deleteOrgSSOConfig(orgId);
            setConfig(null);
            setFormData({
                provider: "okta",
                issuer_url: "",
                client_id: "",
                client_secret: "",
                sso_required: false,
                auto_provision: true,
                default_role: "member",
                allowed_domains: [],
                attribute_mapping: DEFAULT_ATTRIBUTE_MAPPINGS.okta,
                scopes: ["openid", "email", "profile"]
            });
            setShowDeleteConfirm(false);
            setSuccess("SSO configuration deleted");
        } catch (err) {
            console.error("Failed to delete SSO config:", err);
            setError(err.message || "Failed to delete SSO configuration");
        } finally {
            setIsDeleting(false);
        }
    };

    // Copy callback URL
    const handleCopyCallbackUrl = () => {
        const slug = organization?.slug || organization?.name?.toLowerCase().replace(/\s+/g, '-');
        const callbackUrl = `${window.location.origin}/api/auth/sso/${slug}/callback`;
        navigator.clipboard.writeText(callbackUrl);
        setSuccess("Callback URL copied to clipboard");
        setTimeout(() => setSuccess(""), 3000);
    };

    if (isLoading) {
        return (
            <div className="flex items-center justify-center py-12">
                <Loader2 className="w-8 h-8 text-teal-500 animate-spin" />
            </div>
        );
    }

    const providerInfo = getSSOProviderInfo(formData.provider);
    const help = PROVIDER_HELP[formData.provider] || PROVIDER_HELP.generic;

    return (
        <div className="space-y-6">
            {/* Header */}
            <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-indigo-500 to-purple-600 flex items-center justify-center">
                        <Shield className="w-5 h-5 text-white" />
                    </div>
                    <div>
                        <h3 className="text-lg font-semibold dark:text-white text-slate-900">
                            Single Sign-On (SSO)
                        </h3>
                        <p className="text-sm dark:text-slate-400 text-slate-500">
                            Configure enterprise identity provider
                        </p>
                    </div>
                </div>
                {config && (
                    <div className="flex items-center gap-2">
                        <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                            config.status === SSO_STATUS.ENABLED
                                ? "bg-emerald-500/10 text-emerald-500"
                                : config.status === SSO_STATUS.TESTING
                                    ? "bg-amber-500/10 text-amber-500"
                                    : "bg-slate-500/10 text-slate-500"
                        }`}>
                            {config.status === SSO_STATUS.ENABLED ? "Enabled" :
                             config.status === SSO_STATUS.TESTING ? "Testing" : "Disabled"}
                        </span>
                    </div>
                )}
            </div>

            {/* Permission Warning */}
            {!canEdit && (
                <div className="p-3 bg-amber-500/10 border border-amber-500/20 rounded-lg flex items-center gap-2 text-sm text-amber-400">
                    <AlertCircle className="w-4 h-4 flex-shrink-0" />
                    Only organization <strong className="mx-1">admins</strong> and <strong className="mx-1">owners</strong> can configure SSO settings.
                </div>
            )}

            {/* Messages */}
            {error && (
                <div className="p-3 bg-red-500/10 border border-red-500/20 rounded-lg flex items-center gap-2 text-sm text-red-400">
                    <AlertCircle className="w-4 h-4 flex-shrink-0" />
                    {error}
                </div>
            )}
            {success && (
                <div className="p-3 bg-emerald-500/10 border border-emerald-500/20 rounded-lg flex items-center gap-2 text-sm text-emerald-400">
                    <CheckCircle className="w-4 h-4 flex-shrink-0" />
                    {success}
                </div>
            )}

            {/* No config - Setup prompt */}
            {!config && !isEditing && (
                <div className="text-center py-8 dark:bg-slate-800/50 bg-slate-50 rounded-xl border dark:border-slate-700 border-slate-200">
                    <Shield className="w-12 h-12 mx-auto mb-3 dark:text-slate-600 text-slate-300" />
                    <h4 className="dark:text-white text-slate-900 font-medium mb-2">
                        SSO Not Configured
                    </h4>
                    <p className="text-sm dark:text-slate-400 text-slate-500 mb-4 max-w-md mx-auto">
                        Enable Single Sign-On to allow your team members to authenticate using your company&apos;s identity provider.
                    </p>
                    {canEdit && (
                        <button
                            onClick={() => setIsEditing(true)}
                            className="px-4 py-2 bg-indigo-600 hover:bg-indigo-500 text-white rounded-lg text-sm font-medium transition-colors"
                        >
                            Configure SSO
                        </button>
                    )}
                </div>
            )}

            {/* Config exists - Show details or edit form */}
            {(config || isEditing) && (
                <div className="space-y-6">
                    {/* Provider Selection */}
                    <div className="dark:bg-slate-800/50 bg-slate-50 rounded-xl border dark:border-slate-700 border-slate-200 p-4">
                        <label className="text-sm font-medium dark:text-slate-300 text-slate-700 mb-2 block">
                            Identity Provider
                        </label>
                        {isEditing ? (
                            <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                                {PROVIDER_OPTIONS.map(provider => (
                                    <button
                                        key={provider.value}
                                        type="button"
                                        onClick={() => handleChange("provider", provider.value)}
                                        className={`p-3 rounded-lg border text-left transition-all ${
                                            formData.provider === provider.value
                                                ? "border-indigo-500 bg-indigo-500/10"
                                                : "dark:border-slate-600 border-slate-300 hover:border-indigo-500/50"
                                        }`}
                                    >
                                        <div className="font-medium dark:text-white text-slate-900 text-sm">
                                            {provider.label}
                                        </div>
                                        <div className="text-xs dark:text-slate-400 text-slate-500 mt-0.5">
                                            {provider.description}
                                        </div>
                                    </button>
                                ))}
                            </div>
                        ) : (
                            <div className="flex items-center gap-3">
                                <div
                                    className="w-10 h-10 rounded-lg flex items-center justify-center"
                                    style={{ backgroundColor: providerInfo.color }}
                                >
                                    <Building2 className="w-5 h-5 text-white" />
                                </div>
                                <div>
                                    <div className="font-medium dark:text-white text-slate-900">
                                        {providerInfo.name}
                                    </div>
                                    <div className="text-xs dark:text-slate-400 text-slate-500">
                                        {config?.issuer_url}
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>

                    {/* Connection Settings */}
                    <div className="dark:bg-slate-800/50 bg-slate-50 rounded-xl border dark:border-slate-700 border-slate-200 p-4 space-y-4">
                        <h4 className="font-medium dark:text-white text-slate-900 flex items-center gap-2">
                            <Key className="w-4 h-4 text-indigo-500" />
                            Connection Settings
                        </h4>

                        {/* Issuer URL */}
                        <div>
                            <label className="text-sm font-medium dark:text-slate-300 text-slate-700 mb-1 block">
                                Issuer URL
                                {help.docsUrl && (
                                    <a
                                        href={help.docsUrl}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="ml-2 text-indigo-500 hover:text-indigo-400"
                                    >
                                        <ExternalLink className="w-3 h-3 inline" />
                                    </a>
                                )}
                            </label>
                            {isEditing ? (
                                <>
                                    <input
                                        type="url"
                                        value={formData.issuer_url}
                                        onChange={(e) => handleChange("issuer_url", e.target.value)}
                                        placeholder={help.issuerUrl}
                                        className="w-full px-3 py-2 dark:bg-slate-700 bg-white border dark:border-slate-600 border-slate-300 rounded-lg dark:text-white text-slate-900 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
                                    />
                                    <p className="text-xs dark:text-slate-500 text-slate-400 mt-1">
                                        {help.issuerUrl}
                                    </p>
                                </>
                            ) : (
                                <p className="dark:text-slate-300 text-slate-700 text-sm font-mono">
                                    {config?.issuer_url || "—"}
                                </p>
                            )}
                        </div>

                        {/* Client ID */}
                        <div>
                            <label className="text-sm font-medium dark:text-slate-300 text-slate-700 mb-1 block">
                                Client ID
                            </label>
                            {isEditing ? (
                                <input
                                    type="text"
                                    value={formData.client_id}
                                    onChange={(e) => handleChange("client_id", e.target.value)}
                                    placeholder="Enter your OIDC client ID"
                                    className="w-full px-3 py-2 dark:bg-slate-700 bg-white border dark:border-slate-600 border-slate-300 rounded-lg dark:text-white text-slate-900 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
                                />
                            ) : (
                                <p className="dark:text-slate-300 text-slate-700 text-sm font-mono">
                                    {config?.client_id || "—"}
                                </p>
                            )}
                        </div>

                        {/* Client Secret - Only shown when editing */}
                        {isEditing && (
                            <div>
                                <label className="text-sm font-medium dark:text-slate-300 text-slate-700 mb-1 block">
                                    Client Secret
                                    {config && (
                                        <span className="text-xs dark:text-slate-500 text-slate-400 ml-2">
                                            (leave blank to keep current)
                                        </span>
                                    )}
                                </label>
                                <div className="relative">
                                    <input
                                        type={showClientSecret ? "text" : "password"}
                                        value={formData.client_secret}
                                        onChange={(e) => handleChange("client_secret", e.target.value)}
                                        placeholder={config ? "Enter new secret to update" : "Enter your OIDC client secret"}
                                        className="w-full px-3 py-2 pr-10 dark:bg-slate-700 bg-white border dark:border-slate-600 border-slate-300 rounded-lg dark:text-white text-slate-900 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
                                    />
                                    <button
                                        type="button"
                                        onClick={() => setShowClientSecret(!showClientSecret)}
                                        className="absolute right-2 top-1/2 -translate-y-1/2 dark:text-slate-400 text-slate-500 hover:text-slate-700 dark:hover:text-slate-300"
                                    >
                                        {showClientSecret ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                                    </button>
                                </div>
                            </div>
                        )}

                        {/* Callback URL - Always shown */}
                        <div>
                            <label className="text-sm font-medium dark:text-slate-300 text-slate-700 mb-1 block">
                                Callback URL
                                <span className="text-xs dark:text-slate-500 text-slate-400 ml-2">
                                    (configure this in your IdP)
                                </span>
                            </label>
                            <div className="flex items-center gap-2">
                                <code className="flex-1 px-3 py-2 dark:bg-slate-700 bg-slate-100 rounded-lg text-sm dark:text-slate-300 text-slate-700 overflow-x-auto">
                                    {`${window.location.origin}/api/auth/sso/${organization?.slug || 'your-org'}/callback`}
                                </code>
                                <button
                                    type="button"
                                    onClick={handleCopyCallbackUrl}
                                    className="p-2 dark:bg-slate-700 bg-slate-200 rounded-lg dark:text-slate-300 text-slate-600 hover:bg-slate-300 dark:hover:bg-slate-600 transition-colors"
                                >
                                    <Copy className="w-4 h-4" />
                                </button>
                            </div>
                        </div>
                    </div>

                    {/* Access Settings */}
                    <div className="dark:bg-slate-800/50 bg-slate-50 rounded-xl border dark:border-slate-700 border-slate-200 p-4 space-y-4">
                        <h4 className="font-medium dark:text-white text-slate-900 flex items-center gap-2">
                            <Users className="w-4 h-4 text-indigo-500" />
                            Access Settings
                        </h4>

                        {/* SSO Required Toggle */}
                        <div className="flex items-center justify-between">
                            <div>
                                <div className="font-medium dark:text-slate-200 text-slate-800 text-sm">
                                    Require SSO
                                </div>
                                <div className="text-xs dark:text-slate-400 text-slate-500">
                                    Disable password login for this organization
                                </div>
                            </div>
                            {isEditing ? (
                                <button
                                    type="button"
                                    onClick={() => handleChange("sso_required", !formData.sso_required)}
                                    className={`p-1 rounded-full transition-colors ${
                                        formData.sso_required ? "text-indigo-500" : "dark:text-slate-400 text-slate-500"
                                    }`}
                                >
                                    {formData.sso_required ? (
                                        <ToggleRight className="w-8 h-8" />
                                    ) : (
                                        <ToggleLeft className="w-8 h-8" />
                                    )}
                                </button>
                            ) : (
                                <span className={`text-sm font-medium ${
                                    config?.sso_required ? "text-indigo-500" : "dark:text-slate-400 text-slate-500"
                                }`}>
                                    {config?.sso_required ? "Required" : "Optional"}
                                </span>
                            )}
                        </div>

                        {/* Auto Provision Toggle */}
                        <div className="flex items-center justify-between">
                            <div>
                                <div className="font-medium dark:text-slate-200 text-slate-800 text-sm">
                                    Auto-provision users
                                </div>
                                <div className="text-xs dark:text-slate-400 text-slate-500">
                                    Automatically create accounts on first SSO login
                                </div>
                            </div>
                            {isEditing ? (
                                <button
                                    type="button"
                                    onClick={() => handleChange("auto_provision", !formData.auto_provision)}
                                    className={`p-1 rounded-full transition-colors ${
                                        formData.auto_provision ? "text-indigo-500" : "dark:text-slate-400 text-slate-500"
                                    }`}
                                >
                                    {formData.auto_provision ? (
                                        <ToggleRight className="w-8 h-8" />
                                    ) : (
                                        <ToggleLeft className="w-8 h-8" />
                                    )}
                                </button>
                            ) : (
                                <span className={`text-sm font-medium ${
                                    config?.auto_provision ? "text-emerald-500" : "dark:text-slate-400 text-slate-500"
                                }`}>
                                    {config?.auto_provision !== false ? "Enabled" : "Disabled"}
                                </span>
                            )}
                        </div>

                        {/* Default Role */}
                        <div>
                            <label className="text-sm font-medium dark:text-slate-300 text-slate-700 mb-1 block">
                                Default Role for New Users
                            </label>
                            {isEditing ? (
                                <select
                                    value={formData.default_role}
                                    onChange={(e) => handleChange("default_role", e.target.value)}
                                    className="w-full px-3 py-2 dark:bg-slate-700 bg-white border dark:border-slate-600 border-slate-300 rounded-lg dark:text-white text-slate-900 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
                                >
                                    <option value="member">Member</option>
                                    <option value="admin">Admin</option>
                                    <option value="viewer">Viewer</option>
                                </select>
                            ) : (
                                <p className="dark:text-slate-300 text-slate-700 text-sm capitalize">
                                    {config?.default_role || "member"}
                                </p>
                            )}
                        </div>

                        {/* Allowed Domains */}
                        <div>
                            <label className="text-sm font-medium dark:text-slate-300 text-slate-700 mb-1 block">
                                Allowed Email Domains
                                <span className="text-xs dark:text-slate-500 text-slate-400 ml-2">
                                    (leave empty to allow all)
                                </span>
                            </label>
                            {isEditing && (
                                <div className="flex items-center gap-2 mb-2">
                                    <input
                                        type="text"
                                        value={domainInput}
                                        onChange={(e) => setDomainInput(e.target.value)}
                                        onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), handleAddDomain())}
                                        placeholder="company.com"
                                        className="flex-1 px-3 py-2 dark:bg-slate-700 bg-white border dark:border-slate-600 border-slate-300 rounded-lg dark:text-white text-slate-900 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
                                    />
                                    <button
                                        type="button"
                                        onClick={handleAddDomain}
                                        className="px-3 py-2 bg-indigo-600 hover:bg-indigo-500 text-white rounded-lg text-sm font-medium transition-colors"
                                    >
                                        Add
                                    </button>
                                </div>
                            )}
                            <div className="flex flex-wrap gap-2">
                                {formData.allowed_domains.length > 0 ? (
                                    formData.allowed_domains.map(domain => (
                                        <span
                                            key={domain}
                                            className="inline-flex items-center gap-1 px-2 py-1 dark:bg-slate-700 bg-slate-200 rounded-lg text-sm dark:text-slate-300 text-slate-700"
                                        >
                                            <Globe className="w-3 h-3" />
                                            {domain}
                                            {isEditing && (
                                                <button
                                                    type="button"
                                                    onClick={() => handleRemoveDomain(domain)}
                                                    className="ml-1 hover:text-red-400"
                                                >
                                                    &times;
                                                </button>
                                            )}
                                        </span>
                                    ))
                                ) : (
                                    <span className="text-sm dark:text-slate-500 text-slate-400">
                                        All domains allowed
                                    </span>
                                )}
                            </div>
                        </div>
                    </div>

                    {/* Test Result */}
                    {testResult && (
                        <div className={`p-4 rounded-xl border ${
                            testResult.success
                                ? "bg-emerald-500/10 border-emerald-500/20"
                                : "bg-red-500/10 border-red-500/20"
                        }`}>
                            <h4 className={`font-medium mb-2 flex items-center gap-2 ${
                                testResult.success ? "text-emerald-400" : "text-red-400"
                            }`}>
                                {testResult.success ? (
                                    <CheckCircle className="w-4 h-4" />
                                ) : (
                                    <AlertCircle className="w-4 h-4" />
                                )}
                                Connection Test {testResult.success ? "Passed" : "Failed"}
                            </h4>
                            {testResult.success && testResult.endpoints && (
                                <div className="text-sm dark:text-slate-300 text-slate-700 space-y-1">
                                    <p><span className="dark:text-slate-500 text-slate-500">Issuer:</span> {testResult.issuer}</p>
                                    <p><span className="dark:text-slate-500 text-slate-500">Authorization:</span> {testResult.endpoints.authorization}</p>
                                    <p><span className="dark:text-slate-500 text-slate-500">Token:</span> {testResult.endpoints.token}</p>
                                </div>
                            )}
                            {!testResult.success && testResult.error && (
                                <p className="text-sm text-red-400">{testResult.error}</p>
                            )}
                        </div>
                    )}

                    {/* Actions */}
                    <div className="flex items-center justify-between pt-4 border-t dark:border-slate-700 border-slate-200">
                        <div className="flex items-center gap-2">
                            {config && !isEditing && canEdit && (
                                <>
                                    <button
                                        onClick={handleToggleEnabled}
                                        disabled={isSaving}
                                        className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors flex items-center gap-2 ${
                                            config.status === SSO_STATUS.ENABLED
                                                ? "bg-amber-500/10 text-amber-500 hover:bg-amber-500/20"
                                                : "bg-emerald-500/10 text-emerald-500 hover:bg-emerald-500/20"
                                        }`}
                                    >
                                        {isSaving ? (
                                            <Loader2 className="w-4 h-4 animate-spin" />
                                        ) : config.status === SSO_STATUS.ENABLED ? (
                                            <>
                                                <Lock className="w-4 h-4" />
                                                Disable SSO
                                            </>
                                        ) : (
                                            <>
                                                <CheckCircle className="w-4 h-4" />
                                                Enable SSO
                                            </>
                                        )}
                                    </button>
                                    <button
                                        onClick={handleTest}
                                        disabled={isTesting}
                                        className="px-4 py-2 bg-slate-700 hover:bg-slate-600 text-white rounded-lg text-sm font-medium transition-colors flex items-center gap-2"
                                    >
                                        {isTesting ? (
                                            <Loader2 className="w-4 h-4 animate-spin" />
                                        ) : (
                                            <>
                                                <TestTube className="w-4 h-4" />
                                                Test Connection
                                            </>
                                        )}
                                    </button>
                                </>
                            )}
                        </div>

                        <div className="flex items-center gap-2">
                            {isEditing ? (
                                <>
                                    <button
                                        onClick={() => {
                                            setIsEditing(false);
                                            if (config) {
                                                // Reset to current config
                                                setFormData({
                                                    provider: config.provider || "okta",
                                                    issuer_url: config.issuer_url || "",
                                                    client_id: config.client_id || "",
                                                    client_secret: "",
                                                    sso_required: config.sso_required || false,
                                                    auto_provision: config.auto_provision !== false,
                                                    default_role: config.default_role || "member",
                                                    allowed_domains: config.allowed_domains || [],
                                                    attribute_mapping: config.attribute_mapping || DEFAULT_ATTRIBUTE_MAPPINGS[config.provider || "okta"],
                                                    scopes: config.scopes || ["openid", "email", "profile"]
                                                });
                                            }
                                        }}
                                        className="px-4 py-2 dark:bg-slate-700 bg-slate-200 hover:bg-slate-300 dark:hover:bg-slate-600 dark:text-slate-200 text-slate-700 rounded-lg text-sm font-medium transition-colors"
                                    >
                                        Cancel
                                    </button>
                                    <button
                                        onClick={handleSave}
                                        disabled={isSaving}
                                        className="px-4 py-2 bg-indigo-600 hover:bg-indigo-500 text-white rounded-lg text-sm font-medium transition-colors flex items-center gap-2"
                                    >
                                        {isSaving ? (
                                            <Loader2 className="w-4 h-4 animate-spin" />
                                        ) : (
                                            <>
                                                <Save className="w-4 h-4" />
                                                Save Configuration
                                            </>
                                        )}
                                    </button>
                                </>
                            ) : canEdit && (
                                <>
                                    {config && (
                                        <button
                                            onClick={() => setShowDeleteConfirm(true)}
                                            className="px-4 py-2 bg-red-500/10 text-red-400 hover:bg-red-500/20 rounded-lg text-sm font-medium transition-colors flex items-center gap-2"
                                        >
                                            <Trash2 className="w-4 h-4" />
                                            Delete
                                        </button>
                                    )}
                                    <button
                                        onClick={() => setIsEditing(true)}
                                        className="px-4 py-2 bg-indigo-600 hover:bg-indigo-500 text-white rounded-lg text-sm font-medium transition-colors"
                                    >
                                        {config ? "Edit Configuration" : "Configure SSO"}
                                    </button>
                                </>
                            )}
                        </div>
                    </div>
                </div>
            )}

            {/* Delete Confirmation Modal */}
            {showDeleteConfirm && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/80 backdrop-blur-sm p-4">
                    <div className="bg-white dark:bg-slate-900 border dark:border-slate-800 border-slate-200 rounded-2xl w-full max-w-sm shadow-2xl p-6">
                        <div className="flex flex-col items-center text-center space-y-4">
                            <div className="w-12 h-12 bg-red-500/10 text-red-400 rounded-full flex items-center justify-center">
                                <Trash2 className="w-6 h-6" />
                            </div>
                            <div>
                                <h3 className="text-lg font-bold dark:text-white text-slate-900">
                                    Delete SSO Configuration?
                                </h3>
                                <p className="text-sm dark:text-slate-400 text-slate-500 mt-2">
                                    This will disable SSO for your organization. Users will need to use password authentication.
                                </p>
                            </div>
                            <div className="flex gap-3 w-full mt-2">
                                <button
                                    onClick={() => setShowDeleteConfirm(false)}
                                    className="flex-1 px-4 py-2 dark:bg-slate-800 bg-slate-100 hover:bg-slate-200 dark:hover:bg-slate-700 dark:text-slate-200 text-slate-700 rounded-lg text-sm font-medium transition-colors"
                                >
                                    Cancel
                                </button>
                                <button
                                    onClick={handleDelete}
                                    disabled={isDeleting}
                                    className="flex-1 px-4 py-2 bg-red-600 hover:bg-red-500 text-white rounded-lg text-sm font-medium transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
                                >
                                    {isDeleting ? (
                                        <Loader2 className="w-4 h-4 animate-spin" />
                                    ) : (
                                        "Delete"
                                    )}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default OrgSSOSettings;
